/**
 * @typedef {{ focusTab?: boolean }} ByteSplitContentNavActivateOptions
 */

const BYTE_SPLIT_CONTENT_NAV_MOBILE_QUERY = '(max-width: 989px)';

if (!customElements.get('byte-split-content-nav')) {
  class ByteSplitContentNav extends HTMLElement {
    constructor() {
      super();

      /** @type {(event: MouseEvent) => void} */
      this.boundHandleClick = this.handleClick.bind(this);
      /** @type {(event: KeyboardEvent) => void} */
      this.boundHandleKeyDown = this.handleKeyDown.bind(this);
      /** @type {() => void} */
      this.boundHandleViewportChange = this.#handleViewportChange.bind(this);

      this.sectionId = '';
      this.mobileMediaQuery = window.matchMedia(BYTE_SPLIT_CONTENT_NAV_MOBILE_QUERY);

      const emptyHost = document.createElement('div');

      /** @type {NodeListOf<HTMLButtonElement>} */
      this.tabButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (emptyHost.querySelectorAll('button'));
      /** @type {NodeListOf<HTMLElement>} */
      this.panels = /** @type {NodeListOf<HTMLElement>} */ (
        emptyHost.querySelectorAll('[data-split-content-panel]')
      );
    }

    connectedCallback() {
      this.sectionId = this.dataset.splitContentSectionId ?? '';
      this.#cacheElements();
      this.#bindEvents();
      this.#syncAriaControls();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    /**
     * @param {MouseEvent} event
     */
    handleClick(event) {
      const target = event.target;
      const trigger =
        target instanceof Element ? target.closest('[data-split-content-nav-action="select"]') : null;

      if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

      const blockId = trigger.dataset.splitContentBlockId;
      if (!blockId) return;

      const isAlreadyActive =
        trigger.classList.contains('is-active') && trigger.getAttribute('aria-selected') === 'true';

      if (this.#isMobileLayout() && isAlreadyActive) {
        this.#deactivateAll();
        return;
      }

      this.#activateByBlockId(blockId, { focusTab: false });
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleKeyDown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const tabs = this.#getTabButtons();
      if (tabs.length === 0) return;

      const activeElement = document.activeElement;
      const currentIndex =
        activeElement instanceof HTMLButtonElement ? tabs.indexOf(activeElement) : -1;

      if (currentIndex === -1) return;

      event.preventDefault();

      let nextIndex = currentIndex;

      if (event.key === 'ArrowDown') {
        nextIndex = Math.min(currentIndex + 1, tabs.length - 1);
      } else {
        nextIndex = Math.max(currentIndex - 1, 0);
      }

      const nextTab = tabs[nextIndex];

      if (nextTab !== undefined) {
        nextTab.focus();
        const nextId = nextTab.dataset.splitContentBlockId;

        if (nextId) {
          this.#activateByBlockId(nextId);
        }
      }
    }

    #cacheElements() {
      this.tabButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
        this.querySelectorAll('[data-split-content-nav-action="select"]')
      );
      this.panels = /** @type {NodeListOf<HTMLElement>} */ (
        this.querySelectorAll('[data-split-content-panel]')
      );
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      this.addEventListener('keydown', this.boundHandleKeyDown);
      this.mobileMediaQuery.addEventListener('change', this.boundHandleViewportChange);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('keydown', this.boundHandleKeyDown);
      this.mobileMediaQuery.removeEventListener('change', this.boundHandleViewportChange);
    }

    #handleViewportChange() {
      const activeButton = this.querySelector(
        '[data-split-content-nav-action="select"].is-active'
      );
      const blockId =
        activeButton instanceof HTMLElement ? activeButton.dataset.splitContentBlockId : undefined;

      if (blockId) {
        this.#activateByBlockId(blockId, { focusTab: false });
        return;
      }

      if (!this.#isMobileLayout()) {
        const firstTab = this.#getTabButtons()[0];
        const firstBlockId = firstTab?.dataset.splitContentBlockId;

        if (firstBlockId) {
          this.#activateByBlockId(firstBlockId, { focusTab: false });
          return;
        }
      }

      this.#syncAriaControls();
    }

    /**
     * @returns {HTMLButtonElement[]}
     */
    #getTabButtons() {
      return Array.from(this.tabButtons);
    }

    /**
     * @returns {boolean}
     */
    #isMobileLayout() {
      return this.mobileMediaQuery.matches;
    }

    /**
     * @param {string} blockId
     * @param {'inline' | 'stacked'} variant
     * @returns {string}
     */
    #getPanelId(blockId, variant) {
      const prefix = variant === 'inline' ? 'Panel-inline' : 'Panel-stacked';

      return `${prefix}-${blockId}-${this.sectionId}`;
    }

    #syncAriaControls() {
      const variant = this.#isMobileLayout() ? 'inline' : 'stacked';

      for (const button of this.tabButtons) {
        const blockId = button.dataset.splitContentBlockId;

        if (!blockId) continue;

        button.setAttribute('aria-controls', this.#getPanelId(blockId, variant));
      }
    }

    #deactivateAll() {
      const tabs = this.#getTabButtons();

      for (const button of tabs) {
        button.setAttribute('aria-selected', 'false');
        button.classList.remove('is-active');
        button.tabIndex = -1;
      }

      if (tabs[0] !== undefined) {
        tabs[0].tabIndex = 0;
      }

      for (const panel of this.panels) {
        const panelVariant = panel.dataset.splitContentPanelVariant;

        if (panelVariant !== 'inline') continue;

        panel.setAttribute('hidden', '');
        panel.classList.remove('is-active');
      }

      this.#syncAriaControls();
    }

    /**
     * @param {string} blockId
     * @param {ByteSplitContentNavActivateOptions} [options]
     */
    #activateByBlockId(blockId, options = {}) {
      const focusTab = options.focusTab !== false;
      const isMobileLayout = this.#isMobileLayout();

      for (const button of this.tabButtons) {
        const isSelected = button.dataset.splitContentBlockId === blockId;

        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        button.classList.toggle('is-active', isSelected);
        button.tabIndex = isSelected ? 0 : -1;

        if (isSelected && focusTab) {
          button.focus();
        }
      }

      for (const panel of this.panels) {
        const isMatch = panel.dataset.splitContentBlockId === blockId;
        const panelVariant = panel.dataset.splitContentPanelVariant;
        const isVisiblePanel =
          isMatch &&
          ((isMobileLayout && panelVariant === 'inline') ||
            (!isMobileLayout && panelVariant === 'stacked'));

        panel.toggleAttribute('hidden', !isVisiblePanel);
        panel.classList.toggle('is-active', isVisiblePanel);
      }

      this.#syncAriaControls();
    }
  }

  customElements.define('byte-split-content-nav', ByteSplitContentNav);
}
